import { Button, Checkbox, Field, Input, Text } from "@fluentui/react-components";
import {
  Add16Regular,
  ArrowDown16Regular,
  ArrowUp16Regular,
  ChevronDown16Regular,
  ChevronUp16Regular,
  Dismiss16Regular,
} from "@fluentui/react-icons";
import { useState } from "react";
import { OverflowTooltip } from "../../../externalDiagnostics/OverflowTooltip/OverflowTooltip";
import { IMultipleChoiceFormProps } from "./editObjective.types";

export const MultipleChoiceForm = (
  props: IMultipleChoiceFormProps,
): JSX.Element => {
  const [collapsedQuestionIds, setCollapsedQuestionIds] = useState<Set<string>>(
    new Set(),
  );

  const handleToggleCollapsed = (questionId: string) =>
    setCollapsedQuestionIds((previous) => {
      const next = new Set(previous);
      if (next.has(questionId)) next.delete(questionId);
      else next.add(questionId);
      return next;
    });

  return (
    <>
      {props.fieldErrors.questions && (
        <div
          role="alert"
          aria-live="assertive"
          className={props.dialogContentStyles.feedbackError}
        >
          {props.fieldErrors.questions}
        </div>
      )}
      <div className={props.editStyles.questionSectionWrapper}>
        <Field label="Questions" required />
        {props.questions.map((question, questionIndex) => {
          const questionKey = question.id ?? String(questionIndex);
          const isCollapsed = collapsedQuestionIds.has(questionKey);
          const options = question.options ?? [];
          const questionNumber = questionIndex + 1;
          const answersError = question.id
            ? props.fieldErrors[`answers-${question.id}`]
            : undefined;

          return (
            <div
              key={questionKey}
              className={props.editStyles.multipleChoiceCard}
            >
              <div className={props.editStyles.multipleChoiceQuestionRow}>
                <OverflowTooltip text={question.text} overflowSelector="input">
                  <Input
                    value={question.text}
                    placeholder="Enter a question label"
                    disabled={props.isLoading}
                    style={{ width: "100%" }}
                    onChange={(_, data) =>
                      props.onUpdateQuestion(questionIndex, { text: data.value })
                    }
                  />
                </OverflowTooltip>
                <Button
                  icon={<ArrowUp16Regular />}
                  appearance="subtle"
                  disabled={props.isLoading || questionIndex === 0}
                  onClick={() => props.onMoveQuestion(questionIndex, -1)}
                  aria-label={`Move question ${questionNumber} up`}
                />
                <Button
                  icon={<ArrowDown16Regular />}
                  appearance="subtle"
                  disabled={
                    props.isLoading ||
                    questionIndex === props.questions.length - 1
                  }
                  onClick={() => props.onMoveQuestion(questionIndex, 1)}
                  aria-label={`Move question ${questionNumber} down`}
                />
                <Button
                  icon={
                    isCollapsed ? (
                      <ChevronDown16Regular />
                    ) : (
                      <ChevronUp16Regular />
                    )
                  }
                  appearance="subtle"
                  onClick={() => handleToggleCollapsed(questionKey)}
                  aria-expanded={!isCollapsed}
                  aria-label={`${isCollapsed ? "Expand" : "Collapse"} answers for question ${questionNumber}`}
                />
                <Button
                  icon={<Dismiss16Regular />}
                  appearance="subtle"
                  disabled={props.isLoading}
                  onClick={() => props.onRemoveQuestion(questionIndex)}
                  aria-label={`Remove question ${questionNumber}`}
                />
              </div>
              {isCollapsed ? (
                <Text className={props.editStyles.questionsNote}>
                  {answersError ??
                    `${options.length} ${options.length === 1 ? "answer" : "answers"}`}
                </Text>
              ) : (
                <div className={props.editStyles.multipleChoiceOptionsSection}>
                  {options.map((option, optionIndex) => (
                    <div
                      key={optionIndex}
                      className={props.editStyles.multipleChoiceOptionRow}
                    >
                      <OverflowTooltip text={option} overflowSelector="input">
                        <Input
                          value={option}
                          placeholder="Enter an answer label"
                          disabled={props.isLoading}
                          style={{ width: "100%" }}
                          onChange={(_, data) =>
                            props.onUpdateOption(
                              questionIndex,
                              optionIndex,
                              data.value,
                            )
                          }
                        />
                      </OverflowTooltip>
                      <Button
                        icon={<Dismiss16Regular />}
                        appearance="subtle"
                        disabled={props.isLoading}
                        onClick={() =>
                          props.onRemoveOption(questionIndex, optionIndex)
                        }
                        aria-label={`Remove answer ${optionIndex + 1} from question ${questionNumber}`}
                      />
                    </div>
                  ))}
                  <div className={props.editStyles.multipleChoiceAddAnswerRow}>
                    <div className={props.editStyles.addAnswerActions}>
                      <Button
                        icon={<Add16Regular />}
                        appearance="secondary"
                        onClick={() => props.onAddOption(questionIndex)}
                        disabled={
                          props.isLoading ||
                          options.length >= props.maxOptionsCount
                        }
                        className={props.editStyles.addAnswerButton}
                      >
                        Add answer
                      </Button>
                      <Checkbox
                        className={props.editStyles.allowMultipleSelectionCheckbox}
                        label={{
                          children: "Allow multiple selection",
                          className: props.editStyles.allowMultipleSelectionLabel,
                        }}
                        checked={question.allowMultipleSelection ?? true}
                        disabled={props.isLoading}
                        onChange={(_, data) =>
                          props.onUpdateQuestion(questionIndex, {
                            allowMultipleSelection: data.checked === true,
                          })
                        }
                      />
                    </div>
                    {answersError && (
                      <div
                        role="alert"
                        aria-live="assertive"
                        className={props.dialogContentStyles.feedbackError}
                      >
                        {answersError}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        <Button
          icon={<Add16Regular />}
          appearance="secondary"
          onClick={props.onAddQuestion}
          disabled={
            props.isLoading || props.questions.length >= props.maxQuestionsCount
          }
          className={props.editStyles.addQuestionButton}
        >
          Add question
        </Button>
      </div>
    </>
  );
};
